import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BigCTA } from '@/components/BigCTA';
import { uploadDraft } from '@/upload-store';

const key = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;

const MAX_FILES = 6;
const MAX_BYTES = 12 * 1024 * 1024;   // matches bk.llm.max-image-bytes
const MIN_FILES_DAILY = 2;
const MIN_FILES_HISTORICAL = 1;

export function UploadMediaPage() {
  const nav = useNavigate();
  const draft = uploadDraft.get();
  const [files, setFiles] = useState<File[]>(draft.files);
  const [dragOver, setDragOver] = useState(false);
  const [skipped, setSkipped] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const minFiles = draft.type === 'HISTORICAL' ? MIN_FILES_HISTORICAL : MIN_FILES_DAILY;

  // Persist staged files so the back button restores state.
  useEffect(() => () => { uploadDraft.setFiles(files); }, [files]);

  const addFiles = (incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const next = [...files];
    const seen = new Set(next.map(key));
    const skipped: string[] = [];
    for (const f of Array.from(incoming)) {
      if (next.length >= MAX_FILES) { skipped.push(`максимум ${MAX_FILES} файлов`); break; }
      if (!f.type.startsWith('image/')) { skipped.push(`${f.name} — не картинка`); continue; }
      // Reject oversize here: the backend caps at 12 MiB, and finding out after a full
      // upload over mobile data costs the user the whole transfer.
      if (f.size > MAX_BYTES) { skipped.push(`${f.name} — больше 12 МБ`); continue; }
      // Re-opening the picker re-sends the already-staged files; without this the same
      // screenshot is uploaded twice and the pHash duplicate check rejects the report.
      if (seen.has(key(f))) continue;
      seen.add(key(f));
      next.push(f);
    }
    setSkipped(skipped);
    setFiles(next);
  };

  const removeAt = (i: number) => setFiles(files.filter((_, idx) => idx !== i));

  const next = () => {
    uploadDraft.setFiles(files);
    nav('/upload/details');
  };

  return (
    <div className="page">
      <button
        type="button"
        onClick={() => nav('/')}
        className="big-cta secondary"
        style={{ alignSelf: 'flex-start', minHeight: 'auto', padding: '8px 14px', fontSize: 14 }}
      >
        ← Назад
      </button>

      <div>
        <h1 className="page-title">Загрузка отчёта</h1>
        <p className="page-subtitle">
          Сделайте скриншоты экрана со сменой: итог за день + список заказов.
        </p>
      </div>

      <label
        className={`dropzone${dragOver ? ' dragover' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="dz-icon" aria-hidden>📸</div>
        <div className="dz-text">Нажмите, чтобы выбрать скриншоты</div>
        <div className="dz-sub">PNG / JPG · до 12 МБ · от {minFiles} до {MAX_FILES} файлов</div>
      </label>

      {skipped.length > 0 && (
        <div className="error-banner">Не добавлено: {skipped.join('; ')}</div>
      )}

      {files.length > 0 && (
        <div className="thumb-grid">
          {files.map((f, i) => (
            <Thumb key={`${f.name}-${i}`} file={f} onRemove={() => removeAt(i)} />
          ))}
        </div>
      )}

      <div className="sticky-cta">
        <BigCTA disabled={files.length < minFiles} onClick={next}>
          Далее ({files.length}/{MAX_FILES})
        </BigCTA>
      </div>
    </div>
  );
}

function Thumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState<string>('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="thumb">
      {url && <img src={url} alt="" />}
      <button type="button" className="remove" onClick={onRemove} aria-label="Удалить">×</button>
    </div>
  );
}
